// frontend/src/components/common/TranslationModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, CheckCircle, Circle, Search, Save, X, AlertCircle } from 'lucide-react';
import Modal from '../Modal';
import { useTheme } from '../../contexts/ThemeContext';
import axios from '../../axiosConfig';
import referenceService from '../../services/referenceService';

/**
 * TranslationModal Component
 * 
 * Provides a spacious modal interface for managing content translations.
 * - Two-column layout with language list and translation editor
 * - Visual indicators for translation status
 * - Bulk save functionality for all translations
 * - Search/filter capabilities for languages
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {number} props.entityId - Entity ID
 * @param {string} props.entityDefaultLanguage - Default language for the entity
 * @param {string} props.contentType - Type of content (route, entity, etc.)
 * @param {number} props.contentId - ID of the content item
 * @param {string} props.fieldName - Field being translated (name, description)
 * @param {string} props.defaultValue - Current value in default language
 * @param {Function} props.onSave - Callback when translations are saved
 * @param {boolean} props.multiline - Whether to use textarea (default: false)
 */
const TranslationModal = ({
    isOpen,
    onClose,
    entityId,
    entityDefaultLanguage = 'en',
    contentType,
    contentId,
    fieldName,
    defaultValue = '',
    onSave,
    multiline = false,
    isGlobal = false,
    eventTypeId = null,
    channel = null,
    readOnly = false
}) => {
    const { t } = useTranslation();
    const { background, text, border, cn } = useTheme();
    
    // State management
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [translations, setTranslations] = useState({});
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [error, setError] = useState(null);
    
    // Load data when modal opens
    useEffect(() => {
        if (isOpen) {
            loadData();
        }
    }, [isOpen, entityId, contentType, contentId, fieldName, isGlobal, eventTypeId, channel]);
    
    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (isOpen && e.ctrlKey && e.key === 's') {
                e.preventDefault();
                if (hasChanges) {
                    handleSaveAll();
                }
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, hasChanges]);
    
    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            setSelectedLanguage(null);

            const languages = await referenceService.getLanguages();
            setAvailableLanguages(languages);
            const scopeDefaultLanguage = isGlobal ? 'en' : entityDefaultLanguage;

            let transformedTranslations = {};

            if (contentId) {
                // Use entityId from context (even for global, we need to pass the current entityId in URL)
                const apiEntityId = entityId || 1; // Fallback to 1 if null
                const response = await axios.get(`/api/translations/entity/${apiEntityId}/translations`, {
                    params: {
                        contentType,
                        contentId,
                        isGlobal: isGlobal ? 'true' : 'false'
                    }
                });
                const data = response.data.data;
                const fieldTranslations = data.translations[fieldName] || {};

                transformedTranslations = Object.entries(fieldTranslations).reduce((acc, [lang, content]) => {
                    acc[lang] = {
                        content: typeof content === 'string' ? content : content?.content || '',
                        hasChanges: false
                    };
                    return acc;
                }, {});
            }

            setTranslations(transformedTranslations);

            const translatedLanguages = Object.entries(transformedTranslations)
                .filter(([lang, data]) => {
                    if (lang === scopeDefaultLanguage) {
                        return false;
                    }
                    const value = data?.content || '';
                    return Boolean(value && value.trim());
                })
                .map(([lang]) => lang);

            let initialLanguage = translatedLanguages.length > 0 ? translatedLanguages[0] : null;

            if (!initialLanguage) {
                const firstNonDefault = languages.find(l => l.language_code !== scopeDefaultLanguage);
                if (firstNonDefault) {
                    initialLanguage = firstNonDefault.language_code;
                }
            }

            if (initialLanguage) {
                setSelectedLanguage(initialLanguage);
            }

        } catch (error) {
            console.error('Error loading translation data:', error);
            setError('Failed to load translation data');
        } finally {
            setLoading(false);
        }
    };
    
    const handleTranslationChange = (languageCode, value) => {
        setTranslations(prev => ({
            ...prev,
            [languageCode]: {
                content: value,
                hasChanges: true
            }
        }));
        setHasChanges(true);
    };
    
    const handleSaveAll = async () => {
        try {
            setSaving(true);
            setError(null);

            const scopeDefaultLanguage = isGlobal ? 'en' : entityDefaultLanguage;

            // Don't save if no contentId (new content)
            if (!contentId) {
                console.warn('Cannot save translations for new content without contentId');
                onClose();
                return;
            }

            // Prepare translations for API
            const translationsToSave = {
                [fieldName]: {}
            };

            // Only include non-default languages with content
            Object.entries(translations).forEach(([lang, data]) => {
                if (lang !== scopeDefaultLanguage && data.content && data.content.trim()) {
                    translationsToSave[fieldName][lang] = data.content.trim();
                }
            });

            // Use entityId from context (even for global, we need to pass the current entityId in URL)
            const apiEntityId = entityId || 1; // Fallback to 1 if null
            await axios.put(`/api/translations/entity/${apiEntityId}/translations`, {
                contentType,
                contentId,
                translations: translationsToSave,
                isGlobal: isGlobal
            });

            // Reset change flags
            setTranslations(prev => {
                const updated = {};
                Object.entries(prev).forEach(([lang, data]) => {
                    updated[lang] = { ...data, hasChanges: false };
                });
                return updated;
            });
            setHasChanges(false);

            // Call onSave callback if provided
            if (onSave) {
                onSave(translationsToSave);
            }

            // Close modal after successful save
            onClose();

        } catch (error) {
            console.error('Error saving translations:', error);
            setError('Failed to save translations. Please try again.');
        } finally {
            setSaving(false);
        }
    };
    
    const filteredLanguages = availableLanguages.filter(lang =>
        lang.language_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lang.language_code.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    const getTranslationStats = () => {
        const defaultLang = isGlobal ? 'en' : entityDefaultLanguage;
        const nonDefaultLanguages = availableLanguages.filter(l => l.language_code !== defaultLang);
        const translatedCount = nonDefaultLanguages.filter(
            lang => translations[lang.language_code]?.content?.trim()
        ).length;
        return {
            translated: translatedCount,
            total: nonDefaultLanguages.length,
            percentage: nonDefaultLanguages.length > 0 
                ? Math.round((translatedCount / nonDefaultLanguages.length) * 100)
                : 0
        };
    };
    
    const stats = getTranslationStats();
    
    const renderLanguageList = () => {
        return (
            <div className={cn("h-full flex flex-col", border.default, "border-r")}>
                {/* Search bar */}
                <div className={cn("p-3 flex-shrink-0", border.default, "border-b")}>
                    <div className="relative">
                        <Search className={cn("absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4", text.secondary)} />
                        <input
                            type="text"
                            placeholder={t('search_languages')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={cn(
                                "w-full pl-10 pr-3 py-2 rounded-md",
                                background.input,
                                border.default,
                                text.primary,
                                "border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            )}
                        />
                    </div>
                </div>
                
                {/* Language list */}
                <div className="flex-1 overflow-y-auto p-2 min-h-0">
                    {filteredLanguages.map(lang => {
                        if (lang.language_code === entityDefaultLanguage) return null;
                        
                        const isSelected = selectedLanguage === lang.language_code;
                        const hasTranslation = translations[lang.language_code]?.content?.trim();
                        const hasUnsavedChanges = translations[lang.language_code]?.hasChanges;
                        
                        return (
                            <button
                                key={lang.language_code}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedLanguage(lang.language_code);
                                }}
                                type="button"
                                className={cn(
                                    "w-full flex items-center gap-3 p-3 rounded-md mb-1 transition-colors text-left",
                                    isSelected 
                                        ? cn(background.selected, border.selected, "border")
                                        : cn("hover:bg-gray-50 dark:hover:bg-gray-800", border.default),
                                    text.primary
                                )}
                            >
                                {hasTranslation ? (
                                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                ) : (
                                    <Circle className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium truncate">{lang.language_name}</div>
                                    <div className={cn("text-xs", text.secondary)}>{lang.language_code}</div>
                                </div>
                                {hasUnsavedChanges && (
                                    <div className="w-2 h-2 bg-orange-400 rounded-full flex-shrink-0" 
                                         title={t('unsaved_changes')} />
                                )}
                            </button>
                        );
                    })}
                </div>
                
                {/* Progress indicator */}
                <div className={cn("p-3 flex-shrink-0", border.default, "border-t", background.secondary)}>
                    <div className="mb-2">
                        <div className="flex justify-between text-sm mb-1">
                            <span className={text.secondary}>{t('translation_progress')}</span>
                            <span className={text.primary}>
                                {stats.translated}/{stats.total} ({stats.percentage}%)
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${stats.percentage}%` }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    const renderTranslationEditor = () => {
        if (!selectedLanguage) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className={cn("text-center", text.secondary)}>
                        <Languages className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>{t('select_language_to_translate')}</p>
                    </div>
                </div>
            );
        }
        
        const currentLanguage = availableLanguages.find(l => l.language_code === selectedLanguage);
        const currentTranslation = translations[selectedLanguage] || { content: '', hasChanges: false };
        
        return (
            <div className="flex flex-col h-full p-6">
                {/* Header */}
                <div className="mb-4">
                    <h3 className={cn("text-lg font-semibold mb-1", text.primary)}>
                        {t('translate_to', { language: currentLanguage?.language_name })}
                    </h3>
                    <p className={cn("text-sm", text.secondary)}>
                        {t('translation_optional_description')}
                    </p>
                </div>
                
                {/* Original content */}
                <div className="mb-4">
                    <div className={cn("text-sm font-medium mb-2", text.secondary)}>
                        {t('original_text')} ({availableLanguages.find(l => l.language_code === entityDefaultLanguage)?.language_name}):
                    </div>
                    <div className={cn("p-3 rounded-lg whitespace-pre-wrap overflow-auto", background.secondary, border.default, text.primary, "border")} style={{ maxHeight: '120px' }}>
                        {defaultValue || <span className={text.secondary}>({t('no_content')})</span>}
                    </div>
                </div>
                
                {/* Translation input */}
                <div className="flex-1 flex flex-col">
                    <label className={cn("text-sm font-medium mb-2", text.primary)}>
                        {currentLanguage?.language_name} {t('translation')}:
                    </label>
                    {multiline ? (
                        <textarea
                            value={currentTranslation.content}
                            onChange={(e) => handleTranslationChange(selectedLanguage, e.target.value)}
                            readOnly={readOnly}
                            className={cn(
                                "flex-1 p-3 rounded-md resize-none",
                                background.input,
                                border.default,
                                text.primary,
                                "border focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                                readOnly && "cursor-default opacity-75"
                            )}
                            placeholder={t('enter_translation_placeholder')}
                        />
                    ) : (
                        <input
                            type="text"
                            value={currentTranslation.content}
                            onChange={(e) => handleTranslationChange(selectedLanguage, e.target.value)}
                            readOnly={readOnly}
                            className={cn(
                                "p-3 rounded-md",
                                background.input,
                                border.default,
                                text.primary,
                                "border focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                                readOnly && "cursor-default opacity-75"
                            )}
                            placeholder={t('enter_translation_placeholder')}
                        />
                    )}
                    
                    {/* Character count for multiline */}
                    {multiline && (
                        <div className={cn("mt-2 text-sm text-right", text.secondary)}>
                            {currentTranslation.content.length} {t('characters')}
                        </div>
                    )}
                </div>
            </div>
        );
    };
    
    // Prevent event bubbling to parent forms
    const handleModalClick = (e) => {
        e.stopPropagation();
    };
    
    return (
        <div onClick={handleModalClick}>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={
                    <div className="flex items-center gap-2">
                        <Languages className="w-5 h-5" />
                        <span>{t('manage_translations')}</span>
                        <span className={cn("ml-2 px-2 py-1 text-xs rounded-full", background.secondary, text.secondary)}>
                            {stats.translated}/{stats.total}
                        </span>
                    </div>
                }
                size="full"
                footer={
                <div className="flex items-center justify-between">
                    {error && (
                        <div className="flex items-center gap-2 text-red-500 text-sm">
                            <AlertCircle className="w-4 h-4" />
                            <span>{error}</span>
                        </div>
                    )}
                    <div className="flex gap-3 ml-auto">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onClose();
                            }}
                            type="button"
                            className={cn(
                                "px-4 py-2 rounded-md transition-colors",
                                background.secondary,
                                border.default,
                                text.primary,
                                "border hover:bg-gray-100 dark:hover:bg-gray-700"
                            )}
                        >
                            {readOnly ? t('close') : t('cancel')}
                        </button>
                        {!readOnly && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSaveAll();
                                }}
                                type="button"
                                disabled={!hasChanges || saving}
                                className={cn(
                                    "px-4 py-2 rounded-md transition-colors flex items-center gap-2",
                                    hasChanges
                                        ? "bg-blue-500 hover:bg-blue-600 text-white"
                                        : cn("bg-gray-300 dark:bg-gray-600 cursor-not-allowed", text.secondary)
                                )}
                            >
                                <Save className="w-4 h-4" />
                                {saving ? t('saving') : t('save_all_translations')}
                            </button>
                        )}
                    </div>
                </div>
            }
        >
            {loading ? (
                <div className="flex items-center justify-center h-96">
                    <div className={cn("text-center", text.secondary)}>
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-3"></div>
                        <p>{t('loading')}...</p>
                    </div>
                </div>
            ) : (
                <div className="flex -mx-4 -my-4" style={{ height: 'calc(80vh - 200px)', minHeight: '500px' }}>
                    <div className="w-1/3 flex-shrink-0">
                        {renderLanguageList()}
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {renderTranslationEditor()}
                    </div>
                </div>
            )}
        </Modal>
        </div>
    );
};

export default TranslationModal;
